import {
  Box, CircularProgress, Pagination, Typography, useMediaQuery, useTheme
} from '@mui/material'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { TOKENS } from '../../../pages/dashboard-v2/components/primitives'
import { Assignee } from '../../../types/FolderChecklist'
import { IDataTableAction } from '../../widgets/DataTableHeader'
import {
  AssigneesCell, Chip, DateCell, DueCell, ProgressCell
} from './folders/NewDesignCells'
import { RowMenu } from './folders/ProcessListV2'

// processes of one template in the new design (template editor v2, tab Prozesse). The tables
// (ActiveResultsTable / ClosedResultsTable) keep loading the data and their actions; this only
// draws the rows.

export interface ResultRowV2 {
  key: number | string
  // passed to the actions, as in the old table
  row: any
  title: string
  href: string
  auditPoints?: number | null
  due?: string | Date | null
  closedAt?: string | Date | null
  progress?: number | null
  assignees: Assignee[]
  project?: string | null
  offline?: boolean
}

export function toAssignees(users?: any[] | null, teams?: any[] | null): Assignee[] {
  return [
    ...(users ?? []).map((u) => ({ type: 'user' as const, name: u.full_name ?? u.name ?? '', url: u.avatar?.url ?? u.url ?? null })),
    ...(teams ?? []).map((team) => ({ type: 'team' as const, name: team.name ?? '', url: null }))
  ]
}

interface Props {
  mode: 'open' | 'closed'
  rows: ResultRowV2[]
  actions: Array<IDataTableAction<any>>
  page: number
  totalPages: number
  onPagination: (page: number) => void
  isLoading: boolean
  projectLabel: string
  empty: ReactNode
}

const GRID = 'minmax(0,1fr) 170px 120px 150px 140px 44px'

export default function ResultRowsV2({
  mode, rows, actions, page, totalPages, onPagination, isLoading, projectLabel, empty
}: Props) {
  const { t } = useTranslation()
  const theme = useTheme()
  const wide = useMediaQuery(theme.breakpoints.up('md'))
  const head = { fontSize: 12, fontWeight: 600, color: TOKENS.muted }

  const title = (x: ResultRowV2) => (
    <Box sx={{ minWidth: 0 }}>
      <Box
        component={Link}
        to={x.href}
        sx={{
          display: 'block', fontSize: 14, fontWeight: 600, color: TOKENS.text, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover': { color: TOKENS.primary }
        }}
      >
        {x.title}
      </Box>
      <Box sx={{
        display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap'
      }}
      >
        {x.auditPoints !== null && x.auditPoints !== undefined && <Chip tone="primary">{t('templateEditor.auditPoints', { count: x.auditPoints })}</Chip>}
        {x.offline && <Chip>{t('processList.offline')}</Chip>}
      </Box>
    </Box>
  )

  if (isLoading && rows.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
  }

  if (rows.length === 0) {
    return <Typography sx={{ fontSize: 14, color: TOKENS.muted, px: 2.5, py: 2 }}>{empty}</Typography>
  }

  return (
    <Box sx={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity .2s' }}>
      {wide && (
        <Box sx={{
          display: 'grid', gridTemplateColumns: GRID, gap: 2, alignItems: 'center', px: 2.5, py: 1, borderTop: `1px solid ${TOKENS.divider}`
        }}
        >
          <Typography sx={head}>{t('processList.process')}</Typography>
          <Typography sx={head}>{mode === 'open' ? t('processList.due') : t('processList.closedAt')}</Typography>
          <Typography sx={head}>{t('processList.responsible')}</Typography>
          <Typography sx={head}>{projectLabel}</Typography>
          <Typography sx={head}>{t('processList.progress')}</Typography>
          <span />
        </Box>
      )}
      {rows.map((x) => (wide ? (
        <Box
          key={x.key}
          sx={{
            display: 'grid', gridTemplateColumns: GRID, gap: 2, alignItems: 'center', px: 2.5, py: 1.25, borderTop: `1px solid ${TOKENS.divider}`, '&:hover': { bgcolor: '#F7F9FC' }
          }}
        >
          {title(x)}
          <Box>{mode === 'open' ? <DueCell due={x.due as string} /> : <DateCell value={x.closedAt as string} />}</Box>
          <Box sx={{ minWidth: 0 }}><AssigneesCell assignees={x.assignees} /></Box>
          <Typography sx={{
            fontSize: 13, color: x.project ? TOKENS.text : TOKENS.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}
          >
            {x.project || ''}
          </Typography>
          <Box><ProgressCell progress={x.progress} /></Box>
          <Box><RowMenu row={x.row} actions={actions} label={t('processList.actions')} /></Box>
        </Box>
      ) : (
        <Box
          key={x.key}
          sx={{
            display: 'flex', gap: 1.5, alignItems: 'flex-start', px: 2, py: 1.5, borderTop: `1px solid ${TOKENS.divider}`
          }}
        >
          <Box sx={{
            flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75
          }}
          >
            {title(x)}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'
            }}
            >
              {mode === 'open' ? <DueCell due={x.due as string} short /> : <DateCell value={x.closedAt as string} />}
              <AssigneesCell assignees={x.assignees} />
              <Typography sx={{ fontSize: 12.5, color: TOKENS.muted }}>{`${Math.round(x.progress ?? 0)} %`}</Typography>
            </Box>
          </Box>
          <RowMenu row={x.row} actions={actions} label={t('processList.actions')} />
        </Box>
      )))}
      {totalPages > 1 && (
        <Box sx={{
          display: 'flex', justifyContent: 'center', py: 1.5, borderTop: `1px solid ${TOKENS.divider}`
        }}
        >
          <Pagination count={totalPages} page={page} onChange={(_, value) => onPagination(value)} shape="rounded" />
        </Box>
      )}
    </Box>
  )
}
